import React, { useState, FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();

  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [phone, setPhone] = useState(user?.phone || '');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [profileMsg, setProfileMsg] = useState<{ text: string; success: boolean } | null>(null);
  const [passwordMsg, setPasswordMsg] = useState<{ text: string; success: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  const inputClass =
    'appearance-none block w-full px-3 py-2 border border-stake-border rounded-md shadow-sm placeholder-gray-500 bg-dark-200 text-white focus:outline-none focus:ring-stake-blue focus:border-stake-blue sm:text-sm';

  const handleProfileSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    const res = await updateProfile({ first_name: firstName, last_name: lastName, phone });
    setSaving(false);
    setProfileMsg({ text: res.success ? 'הפרטים עודכנו בהצלחה' : res.message || 'שגיאה בעדכון', success: res.success });
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ text: 'הסיסמאות אינן תואמות', success: false });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ text: 'הסיסמא חייבת להכיל לפחות 8 תווים', success: false });
      return;
    }
    setSaving(true);
    const res = await updateProfile({
      first_name: firstName,
      last_name: lastName,
      phone,
      current_password: currentPassword,
      new_password: newPassword,
    });
    setSaving(false);
    if (res.success) {
      setPasswordMsg({ text: 'הסיסמא עודכנה בהצלחה', success: true });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordMsg({ text: res.message || 'שגיאה בעדכון הסיסמא', success: false });
    }
  };

  return (
    <Layout>
      <div className="max-w-lg mx-auto space-y-8">
        <h1 className="text-2xl font-bold text-white">הפרופיל שלי</h1>

        {/* Profile details */}
        <div className="bg-dark-300 rounded-lg p-6 border border-stake-border">
          <h2 className="text-lg font-semibold text-white mb-4">פרטים אישיים</h2>
          <form className="space-y-4" onSubmit={handleProfileSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">שם פרטי</label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">שם משפחה</label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">כתובת מייל</label>
              <input
                type="email"
                disabled
                value={user?.username || ''}
                className={`${inputClass} opacity-50 cursor-not-allowed`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">מספר טלפון</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
              />
            </div>
            {profileMsg && (
              <p className={`text-sm ${profileMsg.success ? 'text-green-400' : 'text-stake-red'}`}>
                {profileMsg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={saving}
              className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-stake-blue hover:bg-stake-blue-dark focus:outline-none focus:ring-2 focus:ring-stake-blue disabled:opacity-50"
            >
              {saving ? 'שומר...' : 'שמור פרטים'}
            </button>
          </form>
        </div>

        {/* Password change */}
        <div className="bg-dark-300 rounded-lg p-6 border border-stake-border">
          <h2 className="text-lg font-semibold text-white mb-4">שינוי סיסמא</h2>
          <form className="space-y-4" onSubmit={handlePasswordSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">סיסמא נוכחית</label>
              <input
                type="password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">סיסמא חדשה</label>
              <input
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">אימות סיסמא חדשה</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputClass}
              />
            </div>
            {passwordMsg && (
              <p className={`text-sm ${passwordMsg.success ? 'text-green-400' : 'text-stake-red'}`}>
                {passwordMsg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={saving}
              className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-stake-blue hover:bg-stake-blue-dark focus:outline-none focus:ring-2 focus:ring-stake-blue disabled:opacity-50"
            >
              {saving ? 'שומר...' : 'עדכן סיסמא'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
